import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { formatarCustoNumero, formatarDinheiro, formatarMarkup, formatarMoeda } from '@/lib/format';

export interface PriceEquationProps {
  /** Titulo da secao (ex.: "Preço sugerido", ou "Preço praticado <span>· simulação</span>"). */
  titulo: ReactNode;
  /** Pill ao lado do titulo (ex.: ProfitBadge). */
  badge?: ReactNode;
  /** Primeira linha: custo x markup = a vista. Todos string decimal, exibidos como vieram. */
  custoUnitario: string;
  markup: string;
  /** Nota abaixo do markup (ex.: "3,3636 × IPI 1,052"). */
  markupNota?: string;
  rotuloMarkup?: string;
  aVista: string;
  /** Segunda linha: a vista + custo financeiro = preco. */
  custoFinanceiro: string;
  custoFinanceiroNota?: string;
  preco: string;
  rotuloPreco?: string;
  /** Rodape opcional: "Sem IPI R$ x + IPI R$ y". */
  semIpi?: string;
  ipi?: string;
  className?: string;
}

function Celula({
  rotulo,
  valor,
  nota,
  destaque = false,
  acento = false,
}: {
  rotulo: string;
  valor: string;
  nota?: string;
  destaque?: boolean;
  acento?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-[10px] px-2 py-3',
        destaque ? 'bg-surface-inverse text-surface-inverse-foreground' : 'bg-surface-muted',
      )}
    >
      <div className={cn('text-caption', destaque ? 'text-line-strong' : 'text-ink-muted')}>{rotulo}</div>
      <div
        className={cn(
          'tabular mt-0.5 font-semibold',
          destaque ? 'text-result' : 'text-metric',
          acento && 'text-primary',
        )}
      >
        {valor}
      </div>
      {nota && <div className="text-[11px] text-ink-muted">{nota}</div>}
    </div>
  );
}

function Operador({ simbolo, leitura }: { simbolo: string; leitura: string }) {
  return (
    <div className="text-[18px] text-ink-disabled">
      <span aria-hidden="true">{simbolo}</span>
      <span className="sr-only">{leitura}</span>
    </div>
  );
}

/** Equacao do preco (custo x markup = a vista; a vista + custo financeiro = preco). Presentacional. */
export function PriceEquation({
  titulo,
  badge,
  custoUnitario,
  markup,
  markupNota,
  rotuloMarkup = 'Markup',
  aVista,
  custoFinanceiro,
  custoFinanceiroNota,
  preco,
  rotuloPreco = 'Preço sugerido',
  semIpi,
  ipi,
  className,
}: PriceEquationProps) {
  const tituloId = useId();
  const linha = 'grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-2.5 text-center';

  return (
    <section
      aria-labelledby={tituloId}
      className={cn('flex flex-col gap-[18px] rounded-2xl border border-border bg-card p-5', className)}
    >
      <div className="flex items-center justify-between gap-3">
        <h2 id={tituloId} className="text-title font-semibold">
          {titulo}
        </h2>
        {badge}
      </div>

      <div role="group" aria-label="Do custo ao valor à vista" className={linha}>
        <Celula rotulo="Custo unitário" valor={formatarCustoNumero(custoUnitario)} />
        <Operador simbolo="×" leitura="vezes" />
        <Celula rotulo={rotuloMarkup} valor={formatarMarkup(markup)} nota={markupNota} acento />
        <Operador simbolo="=" leitura="igual a" />
        <Celula rotulo="À vista" valor={formatarDinheiro(aVista)} />
      </div>

      <div role="group" aria-label="Do valor à vista ao preço" className={linha}>
        <Celula rotulo="À vista" valor={formatarDinheiro(aVista)} />
        <Operador simbolo="+" leitura="mais" />
        <Celula rotulo="Custo financeiro" valor={formatarDinheiro(custoFinanceiro)} nota={custoFinanceiroNota} />
        <Operador simbolo="=" leitura="igual a" />
        <Celula rotulo={rotuloPreco} valor={formatarMoeda(preco)} destaque />
      </div>

      {(semIpi !== undefined || ipi !== undefined) && (
        <div className="text-small text-ink-secondary">
          Sem IPI <b className="tabular text-ink">{formatarMoeda(semIpi)}</b> + IPI{' '}
          <b className="tabular text-ink">{formatarMoeda(ipi)}</b>
        </div>
      )}
    </section>
  );
}
